import { useCallback, useEffect, useState, type FormEvent } from "react";
import { apiFetch, ApiError } from "../../api/client";

export type BoardCheckpointItem = {
  id: string;
  title: string;
  createdAt: string;
  sourceRevision: number;
};

type PendingRestore = {
  id: string;
  title: string;
};

type Props = {
  open: boolean;
  boardId: string;
  busy?: boolean;
  onClose: () => void;
  onSave: (title: string) => Promise<void>;
  onDelete?: (checkpointId: string) => Promise<void>;
  pendingRestore: PendingRestore | null;
  restoreSaveTitle: string;
  onRestoreSaveTitleChange: (v: string) => void;
  onConfirmRestoreSave: () => void;
  onConfirmRestoreSkip: () => void;
  onCancelRestore: () => void;
  onRequestRestore: (item: BoardCheckpointItem) => void;
};

function formatWhen(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("ru-RU", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function defaultSnapshotTitle(): string {
  const d = new Date();
  const date = d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
  const time = d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  return `Снимок ${date}, ${time}`;
}

export function BoardCheckpointsPanel({
  open,
  boardId,
  busy = false,
  onClose,
  onSave,
  onDelete,
  pendingRestore,
  restoreSaveTitle,
  onRestoreSaveTitleChange,
  onConfirmRestoreSave,
  onConfirmRestoreSkip,
  onCancelRestore,
  onRequestRestore,
}: Props) {
  const [items, setItems] = useState<BoardCheckpointItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await apiFetch<{ checkpoints: BoardCheckpointItem[] }>(
        `/api/v1/boards/${encodeURIComponent(boardId)}/checkpoints`
      );
      setItems(Array.isArray(data.checkpoints) ? data.checkpoints : []);
    } catch (e) {
      const msg =
        e instanceof ApiError && e.status === 503
          ? "Снимки ещё не подключены на сервере (нужна миграция БД / перезапуск API)."
          : e instanceof ApiError && e.status === 401
            ? "Нужно войти в аккаунт, чтобы пользоваться снимками."
            : e instanceof ApiError && e.status === 404
              ? "API снимков не найден — пересоберите и перезапустите API."
              : "Не удалось загрузить снимки";
      setLoadError(msg);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [boardId]);

  useEffect(() => {
    if (!open) return;
    setTitle(defaultSnapshotTitle());
    void reload();
  }, [open, reload]);

  useEffect(() => {
    if (!open) setSaving(false);
  }, [open]);

  if (!open && !pendingRestore) return null;

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    const t = title.trim() || defaultSnapshotTitle();
    if (saving || busy) return;
    setSaving(true);
    try {
      await onSave(t);
      setTitle(defaultSnapshotTitle());
      await reload();
    } catch {
      /* ошибка уже показана родителем */
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {pendingRestore ? (
        <div className="wb-checkpoint-banner" role="alertdialog" aria-labelledby="wb-cp-banner-title">
          <div className="wb-checkpoint-banner__body">
            <strong id="wb-cp-banner-title">Сохранить текущее состояние доски?</strong>
            <p className="wb-checkpoint-banner__hint">
              Вы переходите к снимку «{pendingRestore.title}». Текущее содержимое можно сохранить под
              именем, чтобы не потерять.
            </p>
            <label className="wb-checkpoint-banner__label">
              Название снимка
              <input
                className="wb-checkpoint-banner__input"
                type="text"
                maxLength={200}
                value={restoreSaveTitle}
                disabled={busy}
                placeholder="Например: до возврата"
                onChange={(ev) => onRestoreSaveTitleChange(ev.target.value)}
              />
            </label>
          </div>
          <div className="wb-checkpoint-banner__actions">
            <button
              type="button"
              className="wb-btn wb-btn--primary wb-btn--sm"
              disabled={busy || !restoreSaveTitle.trim()}
              onClick={onConfirmRestoreSave}
            >
              Сохранить и перейти
            </button>
            <button
              type="button"
              className="wb-btn wb-btn--ghost wb-btn--sm"
              disabled={busy}
              onClick={onConfirmRestoreSkip}
            >
              Не сохранять
            </button>
            <button
              type="button"
              className="wb-btn wb-btn--ghost wb-btn--sm"
              disabled={busy}
              onClick={onCancelRestore}
            >
              Отмена
            </button>
          </div>
        </div>
      ) : null}

      {open ? (
        <div
          className="wb-checkpoint-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="wb-cp-modal-title"
          onMouseDown={(ev) => {
            if (ev.target === ev.currentTarget) onClose();
          }}
        >
          <div className="wb-checkpoint-overlay__box">
            <div className="wb-checkpoint-modal__head">
              <h2 id="wb-cp-modal-title" className="wb-checkpoint-modal__title">
                Снимки доски
              </h2>
              <button
                type="button"
                className="wb-btn wb-btn--ghost wb-btn--sm wb-checkpoint-modal__close"
                onClick={onClose}
              >
                Закрыть
              </button>
            </div>
            <p className="wb-checkpoint-modal__hint">
              Введите название (или оставьте предложенное) и нажмите «Сохранить текущее».
            </p>

            <form className="wb-checkpoint-save" onSubmit={(e) => void handleSave(e)}>
              <input
                className="wb-checkpoint-save__input"
                type="text"
                maxLength={200}
                value={title}
                disabled={busy || saving}
                placeholder="Название снимка"
                autoFocus
                onChange={(ev) => setTitle(ev.target.value)}
              />
              <button
                type="submit"
                className="wb-modal__btn wb-modal__btn--primary"
                disabled={busy || saving}
              >
                {saving || busy ? "Сохраняю…" : "Сохранить текущее"}
              </button>
            </form>

            {loading ? <p className="wb-checkpoint-list__empty">Загрузка…</p> : null}
            {loadError ? <p className="wb-checkpoint-list__error">{loadError}</p> : null}
            {!loading && !loadError && items.length === 0 ? (
              <p className="wb-checkpoint-list__empty">Пока нет сохранённых снимков</p>
            ) : null}

            <ul className="wb-checkpoint-list">
              {items.map((item) => (
                <li key={item.id} className="wb-checkpoint-list__item">
                  <div className="wb-checkpoint-list__meta">
                    <span className="wb-checkpoint-list__title">{item.title}</span>
                    <span className="wb-checkpoint-list__when">{formatWhen(item.createdAt)}</span>
                  </div>
                  <div className="wb-checkpoint-list__actions">
                    <button
                      type="button"
                      className="wb-modal__btn wb-modal__btn--primary"
                      disabled={busy}
                      onClick={() => onRequestRestore(item)}
                    >
                      Вернуться
                    </button>
                    {onDelete ? (
                      <button
                        type="button"
                        className="wb-modal__btn wb-modal__btn--ghost"
                        disabled={busy}
                        onClick={() => {
                          if (!window.confirm(`Удалить снимок «${item.title}»?`)) return;
                          void onDelete(item.id).then(() => reload()).catch(() => undefined);
                        }}
                      >
                        Удалить
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </>
  );
}
